'use client';

import { t } from '@lingui/macro';
import { Checkbox, CircularProgress, TextField } from '@mui/material';
import { Autocomplete } from '@prismo-io/design-system';
import { useState } from 'react';
import {
  type Control,
  Controller,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import useDebounceValue from '../../../helpers/use-debounce';
import { useDomainsAutocomplete } from './use-domains-autocomplete';

export type DomainsAutocompleteInputProps<F extends FieldValues = FieldValues> =
  {
    name: Path<F>;
    control: Control<F>;
    required?: boolean;
    label?: string;
    helperText?: string;
    disabled?: boolean;
  };

export function DomainsAutocompleteInput<TFieldValues extends FieldValues>(
  props: DomainsAutocompleteInputProps<TFieldValues>
) {
  const {
    name,
    control,
    required = false,
    label = undefined,
    helperText = undefined,
    disabled = false,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [inputTerm, setInputTerm] = useState<string | null>(null);

  const { data: options = [], isLoading } = useDomainsAutocomplete({
    isOpen,
    term: inputTerm,
  });

  const isLoadingDebounced = useDebounceValue(isLoading, 400);

  return (
    <Controller
      name={name}
      control={control}
      render={({
        field: { onChange, onBlur, value },
        fieldState: { error },
      }) => {
        return (
          <Autocomplete
            autoComplete={false}
            open={isOpen}
            onOpen={() => setIsOpen(true)}
            onClose={() => setIsOpen(false)}
            value={value}
            loading={isLoadingDebounced}
            loadingText={t`Recherche des domaines`}
            noOptionsText={t`Aucun domaine trouvé`}
            multiple
            disabled={disabled}
            options={options}
            disableCloseOnSelect
            onChange={(event, value, reason, details) => {
              onChange(value);
            }}
            onInputChange={(event, newValue) => {
              setInputTerm(newValue);
            }}
            onBlur={(event) => {
              onBlur();
            }}
            isOptionEqualToValue={(option, value) => {
              return value ? option.code === (value?.code ?? value) : false;
            }}
            getOptionLabel={(opt) => opt.name}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  size="small"
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                label={label}
                {...params}
                error={!!error}
                InputLabelProps={{
                  ...params.InputLabelProps,
                  required,
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                inputProps={{
                  ...params.inputProps,
                }}
                helperText={error ? error.message : helperText}
              />
            )}
          />
        );
      }}
    />
  );
}
